import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import SmartLink from "../controls/smart-link";
import { useTranslate } from "../../locales/hooks";
import { reachGoal } from "../../analytics/metrika";

type SkillSize = "small" | "medium" | "large";

export interface SkillItem {
  name: string;
  width: string;

  /** Заголовок в модалке (если не задан — возьмём name) */
  title?: string;

  /** Описание/история использования */
  description?: string;

  /** Опциональная ссылка "где посмотреть" */
  link?: string;
  linkLabel?: string;
}

interface ISkillsChartProps {
  skills?: SkillItem[];
  headLabel?: string;
  headLink?: string;
  headLinkLabel?: string;
  size?: SkillSize;
  color?: string;
}

const DEFAULT_COLOR = "#1e3a8a";

const sizeClasses = {
  small: {
    skillText: "text-[5px]",
    headText: "text-[6px]",
    linkText: "text-[5px]",
  },
  medium: {
    skillText: "text-[10px]",
    headText: "text-[10px]",
    linkText: "text-[8px]",
  },
  large: {
    skillText: "text-[15px]",
    headText: "text-[14px]",
    linkText: "text-[12px]",
  },
} as const;

function SkillsModal({
  open,
  title,
  description,
  link,
  linkLabel,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: string;
  link?: string;
  linkLabel?: string;
  onClose: () => void;
}) {
  const t = useTranslate();

  useEffect(() => {
    if (!open) return;

    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      // ✅ перехватываем Esc раньше bubble-слушателей (меню/страница)
      e.preventDefault();
      e.stopPropagation();

      onClose();
    };

    window.addEventListener("keydown", onKeyDownCapture, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDownCapture, {
        capture: true,
      });
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* overlay */}
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* dialog */}
      <div className="relative mx-auto mt-[10vh] w-[min(92vw,560px)] rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-sm hover:bg-slate-50"
          >
            ✕
          </button>
        </div>

        <div className="p-4">
          {description ? (
            <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {description}
            </p>
          ) : (
            <p className="text-sm text-slate-500">{t("skillsModal__noDescription")}</p>
          )}

          {link && (
            <div className="mt-4">
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="text-sm underline decoration-solid"
              >
                {linkLabel ?? "Ссылка"}
              </a>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            {t("skillsModal__close")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const SkillsChart: FunctionComponent<ISkillsChartProps> = (props) => {
  const t = useTranslate();

  const {
    skills = [],
    headLabel = t("headLabel__defaultText"),
    headLink,
    headLinkLabel = t("headLinkLabel__defaultText"),
    size = "medium",
    color = DEFAULT_COLOR,
  } = props;

  const classes = sizeClasses[size];

  const [selected, setSelected] = useState<SkillItem | null>(null);

  const modalTitle = useMemo(() => {
    if (!selected) return "";
    return selected.title ?? selected.name;
  }, [selected]);

  return (
    <>
      <div className="relative flex w-full min-w-[200px] shrink-0 items-center justify-center">
        {/* Tail (bars) */}
        <div className="flex min-h-px min-w-px flex-[1_0_0] flex-col items-center justify-center gap-[2px]">
          {skills.map((skill, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                reachGoal("skills_modal");
                setSelected(skill);
              }}
              className="group relative flex w-full shrink-0 items-start justify-end text-left hover:opacity-95"
              title={skill.description ? t("skillsModal__clickHint") : undefined}
            >
              <div
                className="relative flex items-center justify-end rounded-bl-[10px] rounded-tl-[10px] transition-all duration-500 ease-out"
                style={{ width: skill.width, backgroundColor: color }}
              >
                <div className="relative flex min-h-px min-w-px flex-[1_0_0] items-center justify-center py-[3px]">
                  <div
                    className={`relative flex min-h-px min-w-px flex-[1_0_0] flex-col justify-center text-right font-['Manrope:Bold',sans-serif] font-bold leading-[0] text-[#f8fafc] ${classes.skillText}`}
                  >
                    <p className="leading-[normal] whitespace-pre-wrap decoration-dashed decoration-[#f8fafc]/70 underline-offset-[3px] group-hover:underline">
                      {skill.name}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Head (circle) */}
        <div className="flex self-stretch">
          <div
            className="flex h-full w-full min-w-px shrink-0 items-center justify-center rounded-br-[100px] rounded-tr-[100px] px-[10px]"
            style={{ backgroundColor: color }}
          >
            <div className="relative shrink-0 whitespace-pre-line text-center font-['Manrope:Bold',sans-serif] font-bold leading-[normal] text-[#f8fafc]">
              <p className={`mb-0 ${classes.headText}`}>{headLabel}</p>

              {headLink && (
                <SmartLink
                  href={headLink}
                  target="_blank"
                  className={`decoration-solid underline ${classes.linkText}`}
                >
                  {headLinkLabel}
                </SmartLink>
              )}
            </div>
          </div>
        </div>
      </div>

      <SkillsModal
        open={!!selected}
        title={modalTitle}
        description={selected?.description}
        link={selected?.link}
        linkLabel={selected?.linkLabel}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

export { SkillsChart };
export type { ISkillsChartProps };
